'use client';

import { ExclamationCircleIcon as ExclamationCircleIconOutline } from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import { RetainedStateView } from '~/components/Loading/RetainedStateView';
import Skeleton from '~/components/Loading/Skeleton';
import PendingLink from '~/components/Navigation/PendingLink';
import Pagination, { usePagination } from '~/components/Pagination';
import Quote from '~/components/Quote';
import { Routes, UNKNOWN_BRAND_ID } from '~/config';
import { useWorkspace } from '~/contexts/WorkspaceContext';
import { cn } from '~/lib/utils';
import { api, skipBatchOption } from '~/utils/api';
import { dateFormatters } from '~/utils/dateUtils';

type RiskCardProps = {
  brand:
    | {
        id: string;
        name: string;
      }
    | undefined
    | null;
  policyName: string;
  createdAt: Date;
  text: string | null;
  explanation: string;
  documentId: string;
  report:
    | {
        id: string;
        name: string;
      }
    | null
    | undefined;
  compact?: boolean;
  /** Skip pending-nav skeleton so drawer links aren't unmounted mid-click. */
  skipPending?: boolean;
};

const RiskCard = (props: RiskCardProps) => {
  const { workspaceId } = useWorkspace();
  const href = `/${workspaceId}${Routes.CONTENT_SCANNER}/brand-reports/${props.brand?.id || UNKNOWN_BRAND_ID}/report/${props.report?.id}/scanned-page/${props.documentId}`;

  if (props.compact) {
    return (
      <PendingLink
        href={href}
        skipPending={props.skipPending}
        className="hover:bg-muted focus-visible:ring-ring/50 flex items-center gap-3 px-4 py-2.5 outline-none focus-visible:ring-[3px] focus-visible:ring-inset"
      >
        <span aria-hidden className="bg-destructive size-1.5 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1">
          <div className="text-foreground truncate text-sm font-medium">{props.policyName}</div>
          <div className="text-muted-foreground mt-0.5 truncate text-xs">
            {[props.brand?.name || 'Unknown Brand', props.report?.name].filter(Boolean).join(' · ')}
          </div>
        </div>
        <span
          className="text-muted-foreground shrink-0 text-xs tabular-nums"
          title={dateFormatters.fullDateTime(props.createdAt)}
        >
          {dateFormatters.relativeTime(props.createdAt)}
        </span>
      </PendingLink>
    );
  }

  return (
    <PendingLink
      href={href}
      skipPending={props.skipPending}
      className="border-border/80 bg-card hover:border-destructive/40 focus-visible:ring-ring/50 block rounded-xl border p-3 shadow-[0_1px_2px_rgba(0,0,0,0.04)] outline-none transition-shadow duration-150 hover:shadow-[0_4px_14px_rgba(0,0,0,0.07)] focus-visible:ring-[3px]"
    >
      <div className="mb-2 flex w-full items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <ExclamationCircleIconOutline className="text-destructive size-4 shrink-0" />
          {props.report?.name && (
            <span className="bg-muted text-muted-foreground truncate rounded-md px-2 py-0.5 text-xs font-medium">
              {props.report.name}
            </span>
          )}
          <span className="text-muted-foreground truncate text-xs font-medium">
            {props.brand?.name || 'Unknown Brand'}
          </span>
          <span className="text-foreground truncate text-xs font-semibold">
            {props.policyName}
          </span>
        </div>
        <span
          className="text-muted-foreground shrink-0 text-xs tabular-nums"
          title={dateFormatters.fullDateTime(props.createdAt)}
        >
          {dateFormatters.relativeTime(props.createdAt)}
        </span>
      </div>
      {props.text && (
        <Quote
          text={props.text}
          className="bg-muted/50 text-foreground my-2 line-clamp-2 rounded-lg text-sm"
          title={props.text}
        />
      )}
      <div className="text-muted-foreground text-sm">{props.explanation}</div>
    </PendingLink>
  );
};

type RecentHighRiskIssuesProps = {
  reportId?: string;
  /** Cap results (default 5). */
  limit?: number;
  /** Hide pagination and densify empty / cards for Home. */
  compact?: boolean;
  /** Show the built-in section title (default true). */
  showHeader?: boolean;
  /** Optional “View all” destination when compact. */
  viewAllHref?: string;
};

type PaginationInfoState = {
  totalCount: number;
  totalPages: number;
};

export default function RecentHighRiskIssues({
  reportId,
  limit = 5,
  compact = false,
  showHeader = true,
  viewAllHref,
}: RecentHighRiskIssuesProps) {
  const itemsPerPage = limit;
  const { currentPage, setCurrentPage } = usePagination(itemsPerPage);

  const [paginationInfo, setPaginationInfo] = useState<PaginationInfoState>({
    totalCount: 0,
    totalPages: 1,
  });

  const { data: highRiskIssuesResponse, isLoading } = api.scanner.getHighRiskIssues.useQuery(
    {
      reportId,
      page: compact ? 1 : currentPage,
      limit: itemsPerPage,
    },
    skipBatchOption,
  );

  const highRiskIssues = highRiskIssuesResponse?.items || [];

  useEffect(() => {
    if (highRiskIssuesResponse && !isLoading) {
      setPaginationInfo({
        totalCount: highRiskIssuesResponse.totalCount || 0,
        totalPages: highRiskIssuesResponse.totalPages || 1,
      });
    }
  }, [highRiskIssuesResponse, isLoading]);

  return (
    <div>
      {showHeader && (
        <div className="mb-2 flex w-full items-baseline justify-between gap-3">
          <h3 className="text-foreground text-sm font-semibold tracking-tight">
            High-Risk Issues
          </h3>
          {compact && viewAllHref && (
            <PendingLink
              href={viewAllHref}
              className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 text-xs outline-none focus-visible:ring-[3px]"
            >
              View all
            </PendingLink>
          )}
        </div>
      )}
      {isLoading ? (
        <div className={compact ? 'divide-border divide-y' : 'flex flex-col gap-3'}>
          {[...Array(compact ? Math.min(limit, 3) : 5)].map((_, i) => (
            <Skeleton
              key={i}
              className={cn(
                'w-full',
                compact ? 'mx-4 my-2.5 h-8 rounded-md' : 'h-24 rounded-xl',
              )}
            />
          ))}
        </div>
      ) : (
        <RetainedStateView
          data={highRiskIssues}
          isLoading={isLoading}
          className={compact ? 'divide-border divide-y' : 'space-y-3'}
          renderItem={(i) => (
            <RiskCard
              key={i.id}
              policyName={i.policy.name}
              brand={i.document.brand}
              createdAt={i.createdAt}
              explanation={i.explanation}
              text={i.text}
              report={i.document.ScannerRunDocuments[0]?.ScannerReport}
              documentId={i.document.id}
              compact={compact}
              skipPending={!!reportId}
            />
          )}
          renderEmpty={() =>
            compact ? (
              <p className="text-muted-foreground px-4 py-5 text-center text-sm" role="status">
                No High-Risk Issues.
              </p>
            ) : (
              <div
                role="status"
                className="border-border bg-card text-muted-foreground flex items-center gap-2 rounded-xl border border-dashed px-4 py-5 text-sm"
              >
                <InformationCircleIcon height={18} className="text-muted-foreground size-[18px] shrink-0" />
                <span>No high-risk issues detected.</span>
              </div>
            )
          }
        />
      )}

      {!compact && paginationInfo.totalCount > 0 && (
        <div className="mt-4">
          <Pagination
            dense
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            total={paginationInfo.totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={paginationInfo.totalCount}
            isLoading={isLoading}
            itemLabel="issues"
            rangeOnly
          />
        </div>
      )}
    </div>
  );
}
